import Image from 'next/image';
import React, { forwardRef, memo, MutableRefObject } from 'react';
import styled from 'styled-components';
import backgroundImg from 'public/asset/product/deepfield/00-main-visual/product-main-bg@2x.png';
import {
  ImgBackgroundWrapper,
  MainVisualInnerContainer,
  MainVisualOuterContainer,
} from 'components/Common/CommonWrappers';
import {
  BodyTextLStrong,
  BodyTextS,
  H1,
  SubtitleM,
  BB,
  GRAY50,
} from 'global-styles';
import { useRouter } from 'next/router';
import { BlueLabelBox } from 'components/Common/BlueLabelBox';
import { DeepfieldIcon, PulsarIcon } from 'assets/icons';
import { useMemo } from 'react';
import { txtAnimation } from 'components/Home/BannerItems';

const KO = () => (
  <>
    <div className="ko-wrapper-pc">
      <SubtitleM>
        <div>블루닷 제품으로 비용을 절감하며 화질을 개선하고</div>
        <div>만족도 높은 시청경험을 제공해보세요.</div>
      </SubtitleM>
    </div>
    <div className="ko-wrapper-mo">
      <SubtitleM>
        <div>블루닷 제품으로 비용을 절감하며</div> <div>화질을 개선하고</div>
        <div>만족도 높은 시청경험을 제공해보세요.</div>
      </SubtitleM>
    </div>
  </>
);

const EN = () => (
  <>
    <div className="en-wrapper-pc">
      <SubtitleM>
        <span>With BLUEDOT&apos;s product, you can reduce the cost,</span>
      </SubtitleM>
      <SubtitleM>
        <span>improve video quality, and provide users</span>
      </SubtitleM>
      <SubtitleM>
        <span>with a better viewing experience.</span>
      </SubtitleM>
    </div>
    <div className="en-wrapper-mo">
      <SubtitleM>
        <span>With BLUEDOT&apos;s product,</span>
      </SubtitleM>
      <SubtitleM>
        <span>you can reduce the cost,</span>
      </SubtitleM>
      <SubtitleM>
        <span>improve video quality, and provide users</span>
      </SubtitleM>
      <SubtitleM>
        <span>with a better viewing experience.</span>
      </SubtitleM>
    </div>
  </>
);

const DeepfieldMainVisual = forwardRef((_, ref) => {
  const { locale, push } = useRouter();
  const boxWrapperItems = useMemo(
    () => [
      {
        productName: 'DeepField',
        hashListItemsKo: ['#영상&이미지', '#화질개선', '#업스케일링'],
        hashListItemsEn: ['#video&image', '#improvement', '#upscaling'],
        icon: <DeepfieldIcon />,
        onClick: () =>
          push(
            {
              pathname: '/product/sr1sr2',
              hash: encodeURIComponent('product-section'),
            },
            undefined,
            { scroll: false }
          ),
      },
      {
        productName: 'Pulsar',
        hashListItemsKo: ['#영상&이미지', '#인코딩', '#비용절감'],
        hashListItemsEn: ['#video&image', '#encoding', '#reduced cost'],
        icon: <PulsarIcon />,
        onClick: () =>
          push(
            { pathname: '/product/av1', hash: '#product-section' },
            undefined,
            {
              scroll: false,
            }
          ),
      },
    ],
    [push]
  );

  return (
    <DeepfieldMainVisualContainer
      ref={ref as MutableRefObject<HTMLDivElement>}
      className="main-section-container"
    >
      <ImgBackgroundWrapper>
        <Image
          src={backgroundImg.src}
          layout="fill"
          alt=""
          className="target-img"
          objectFit="cover"
          loading="eager"
          priority
          quality={70}
          sizes="(min-width: 906px) 90vw,
                 (min-width: 600px) 60vw,
                 (max-width: 599px) 45vw"
        />
      </ImgBackgroundWrapper>

      <MainVisualInnerContainer>
        <H1 className="main-typo" as="h2">
          Product
        </H1>

        <div className="desc-text-wrapper">
          <div className="anim-wrapper">
            {locale === 'en' ? <EN /> : <KO />}
          </div>
        </div>

        <div className="box-wrapper">
          {boxWrapperItems.map(item => (
            <ProductBlueLabelBox
              className="product-main-box"
              tag="button"
              onClick={item.onClick}
              key={item.productName}
            >
              <ProductSubAnchor>
                <span className="top-wrapper">
                  <span>{item.icon}</span>
                  <BodyTextLStrong className="product-name">
                    {item.productName}
                  </BodyTextLStrong>
                </span>

                <ul className="hash-list">
                  {(locale === 'en'
                    ? item.hashListItemsEn
                    : item.hashListItemsKo
                  ).map(hashItem => (
                    <BodyTextS as="li" key={hashItem}>
                      {hashItem}
                    </BodyTextS>
                  ))}
                </ul>
              </ProductSubAnchor>
            </ProductBlueLabelBox>
          ))}
        </div>
      </MainVisualInnerContainer>
    </DeepfieldMainVisualContainer>
  );
});

DeepfieldMainVisual.displayName = 'DeepfieldMainVisual';
export default memo(DeepfieldMainVisual);

const DeepfieldMainVisualContainer = styled(MainVisualOuterContainer)`
  .box-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 56px;
    animation: ${txtAnimation} 1.5s 1.6s cubic-bezier(0.4, 0, 0.2, 1) backwards;
  }
  .product-main-box + .product-main-box {
    margin: 0 0 0 16px;
  }
  .ko-wrapper-pc {
    display: block;
  }
  .ko-wrapper-mo {
    display: none;
  }
  @media screen and (max-width: 904px) {
    .box-wrapper {
      flex-direction: column;
    }
    .product-main-box + .product-main-box {
      margin: 16px 0 0;
    }
  }

  @media screen and (max-width: 599px) {
    .ko-wrapper-pc {
      display: none;
    }
    .ko-wrapper-mo {
      display: block;
    }
  }
`;

const ProductBlueLabelBox = styled(BlueLabelBox)`
  @media screen and (max-width: 599px) {
    width: 328px;
  }
`;

const ProductSubAnchor = styled.span`
  display: block;
  padding: 18px 16px;
  .top-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .product-name {
    margin-left: 4px;
    color: ${BB};
  }
  .hash-list {
    color: ${GRAY50};
    display: flex;
    justify-content: center;
  }
  .hash-list > li + li {
    margin: 0 0 0 8px;
  }
`;
